import { useState } from "react";
import { joinWaitlist } from "@/lib/api";
import { useT } from "@/context/LanguageContext";

/**
 * WaitlistBlock — appears on PDP when the selected variant is out of stock.
 * Guest-friendly: only needs email. Fully translated (EN / PT / ES).
 *
 * The `variantName` is displayed verbatim (it is already formatted as
 * "Colour / Size" or "Colour" or "Size" by the admin) — we no longer
 * prepend the misleading word "Size".
 */
const WaitlistBlock = ({ productSlug, variantName }) => {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    setError(null);
    try {
      await joinWaitlist(productSlug, variantName, email.trim());
      setState("done");
    } catch (err) {
      setError(err?.response?.data?.detail || t("waitlist.error"));
      setState("error");
    }
  };

  return (
    <div className="mt-6 border border-[#F1DCC5] bg-white rounded-2xl p-5" data-testid="waitlist-block">
      <div className="font-badge text-[10px] tracking-[0.28em] uppercase text-[var(--gl-orange-hover)]">
        {t("waitlist.oosLabel")}
      </div>
      <h3 className="font-serif-display text-xl md:text-2xl mt-1.5 text-[#241C18]">
        {t("waitlist.title")}
      </h3>
      <p className="mt-1 text-sm text-[#8A7568]">
        {variantName ? <><span className="text-[#241C18]">{variantName}</span> — </> : null}
        {t("waitlist.body")}
      </p>

      {state === "done" ? (
        <div className="mt-4 text-sm font-typewriter tracking-wide text-[#241C18]" data-testid="waitlist-confirmed">
          {t("waitlist.confirmed")}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            className="input-editorial flex-1"
            placeholder={t("waitlist.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="waitlist-email-input"
          />
          <button
            type="submit"
            disabled={state === "loading"}
            className="btn-orange whitespace-nowrap"
            data-testid="waitlist-submit-btn"
          >
            {state === "loading" ? t("waitlist.ctaLoading") : t("waitlist.cta")}
          </button>
        </form>
      )}
      {state === "error" && error && (
        <div className="mt-3 text-sm text-[#C97A63] font-typewriter">{error}</div>
      )}
    </div>
  );
};

export default WaitlistBlock;
